/* eslint-disable no-console */
import { Helper } from '../mixins/helpers';
import { useUserStore } from '@/stores/user';
import { useCoursesStore } from '@/stores/courses';
import { useAuthStore } from '@/stores/auth';
import { usePathwaysStore } from '@/stores/pathways';
import { useMinorConsentStore } from '@/stores/minorConsent';
import { useHsGradDateStore } from '@/stores/hsGradDate';
import { toReportableError } from '@/utils/hsGraduation';
import { useViewAsStore } from '@/stores/viewAs';
import { isFeatureEnabled } from '../../feature-flags';
import { projectPreregisteredRuns, mergePreregisteredUpcoming } from '@/components/courses/preregisteredCourses';
import { captureException, captureMessage } from '@/services/sentry.service';
import { createCache, safeWebStorage } from '@/utils/cache';

// ACC-352: resolve minor-consent status once per page load, on any authenticated entry.
// The login flow preloads learnerData (Callback.vue), so this must not be gated on a cold init.
let minorConsentStatusResolved = false;

// ACC-411: persist consent resolution across page loads so a hard refresh doesn't re-hit the
// server. The status GET is not a free read (api-hub findOrCreatePendingRecord = write attempt),
// so we cache it in two tiers, each keyed by learner id (passed as the cache key below) so nothing
// leaks across accounts on a shared device:
//   - satisfiedCache: "satisfied" is a terminal, one-way state (a minor never un-consents; a
//     non-minor is never gated), so it's stored durably (no TTL) — once set we never call again.
//   - pendingCache: the transient "pending" status, stored with a 1-day TTL and re-resolved after.
const consentStorage = safeWebStorage('local');
const satisfiedCache = createCache({ storage: consentStorage, namespace: 'minorConsent:satisfied' });
const pendingCache = createCache({
  storage: consentStorage,
  namespace: 'minorConsent:pending',
  ttlMs: 24 * 60 * 60 * 1000 // 1 day
});

// ACC-411: once consent is recorded (submit success) promote the learner to the durable
// "satisfied" tier and drop any stale pending entry. Without this, minorConsent.clear() only
// resets the in-memory store while the pending entry survives, so the next hard refresh within
// the TTL takes the Tier-2 cache hit, re-marks the gate required, and re-blocks a learner who
// has already consented (a hard block once past the grace window). Mirrors the promotion path
// in ensureMinorConsentStatus so cache and server can never disagree.
export function markMinorConsentSatisfied(learnerId) {
  if (!learnerId) return;
  satisfiedCache.set(learnerId, true);
  pendingCache.remove(learnerId);
}

// ACC-399 (#1616): re-resolve the gate for whoever `conn.learnerData` currently is,
// bypassing the once-per-load guard. Used when the effective learner changes without a
// page reload — i.e. entering/leaving View-As — so an admin sees the exact gate the
// viewed-as learner would (or the admin's own, none, on exit). Fails open: a fetch
// error clears the gate rather than blocking the admin.
export async function refreshMinorConsentStatus(conn) {
  if (!isFeatureEnabled('MINOR_CONSENT_GATE')) return;
  const store = useMinorConsentStore();
  try {
    const status = await conn.getLearnerConsentStatus('enrollment');
    store.clear();
    store.setStatus(status);
  } catch (consentErr) {
    store.clear();
    captureException(consentErr, {
      logToConsole: true,
      context: { tags: { source: 'minor_consent_status_viewas' } }
    });
  }
}

// ULC-3523: resolve the graduation date gate for whoever `conn.learnerData` is now —
// on page load, and again on entering/leaving View-As so the admin sees the viewed
// learner's gate. Fails open: an error (or the API flag being off) shows no modal.
let hsGradDateStatusResolved = false;
// Bumped per refresh so a slower, older response (e.g. the viewed learner's after leaving View-As) is dropped.
let hsGradDateRequestSeq = 0;

export async function refreshHsGradDateStatus(conn) {
  const store = useHsGradDateStore();
  store.clear();
  const requestSeq = ++hsGradDateRequestSeq;
  const learnerId = conn?.learnerData?.learnerId;
  if (!isFeatureEnabled('HS_GRAD_DATE_GATE', true) || !learnerId) return;

  const isCurrent = () => requestSeq === hsGradDateRequestSeq && conn?.learnerData?.learnerId === learnerId;
  store.setPending(true);
  try {
    const status = await conn.getHsGraduationDateStatus();
    if (isCurrent()) store.setStatus(status);
  } catch (err) {
    // 404 = API flag off; 403 = this caller can't see the learner. Neither is worth a report.
    if (isCurrent() && ![403, 404].includes(err?.status)) {
      captureException(toReportableError(err, 'hs_grad_date_status'), {
        logToConsole: true,
        context: { tags: { source: 'hs_grad_date_status' } }
      });
    }
  } finally {
    if (requestSeq === hsGradDateRequestSeq) store.setPending(false);
  }
}

export const SessionInitializer = {
  computed: {
    verbose() {
      return !!document.cookie.match(/verbose-logging=enabled/);
    }
  },
  methods: {
    // Helper method to get stores
    getStores() {
      return {
        userStore: useUserStore(),
        coursesStore: useCoursesStore(),
        authStore: useAuthStore(),
        pathwaysStore: usePathwaysStore()
      };
    },
    // ACC-352 / ACC-411: populate the gate store from consent status, resolving exactly once
    // per page load and reusing a persisted result (see the two-tier cache above) so a hard
    // refresh doesn't re-hit the server. Falls back to a fresh fetch whenever the cache can't
    // safely apply (View-As, no learner id, or a storage error).
    async ensureMinorConsentStatus() {
      if (!isFeatureEnabled('MINOR_CONSENT_GATE') || minorConsentStatusResolved) return;
      const store = useMinorConsentStore();
      // Same id the status GET is scoped by (connector.getLearnerConsentStatus → ea_learner_application),
      // so the cache key can never disagree with the response it caches.
      const learnerId = this.$conn.learnerData?.learnerId;

      // View-As (or an unknown learner) never uses the cache: refreshMinorConsentStatus owns the
      // proxy path and must always show the effective learner's live status, not a cached one.
      const canUseCache = !this.$conn._isProxy && !!learnerId;

      if (canUseCache) {
        // Tier 1: satisfied is terminal — skip the server call for good.
        if (satisfiedCache.get(learnerId)) {
          store.setStatus({});
          minorConsentStatusResolved = true;
          return;
        }
        // Tier 2: reuse a still-pending status within its TTL.
        const cachedPending = pendingCache.get(learnerId);
        if (cachedPending) {
          store.setStatus(cachedPending);
          minorConsentStatusResolved = true;
          return;
        }
      }

      try {
        const status = await this.$conn.getLearnerConsentStatus('enrollment');
        store.setStatus(status);
        if (canUseCache) {
          if (store.required) {
            pendingCache.set(learnerId, status);
          } else {
            // Promote to the durable tier and drop any now-stale pending entry.
            satisfiedCache.set(learnerId, true);
            pendingCache.remove(learnerId);
          }
        }
        minorConsentStatusResolved = true;
      } catch (consentErr) {
        captureException(consentErr, {
          logToConsole: true,
          context: { tags: { source: 'minor_consent_status' } }
        });
      }
    },
    // Once per page load; doesn't hold up the dashboard (the modal appears when it resolves).
    ensureHsGradDateStatus() {
      if (hsGradDateStatusResolved) return;
      hsGradDateStatusResolved = true;
      refreshHsGradDateStatus(this.$conn);
    },
    isGpaCourseCalculatorEligible(enrollment) {
      return (
        enrollment._courseInfo &&
        (this.courseEndDateinFuture(enrollment) ||
          this.courseEndDateinPastWithCreditEligible(enrollment) ||
          this.courseEndDateinPastWithCreditPending(enrollment) ||
          this.continuousCourseCheckForGpaCourseCalculator(enrollment)) &&
        (!enrollment._courseCompletedDate ||
          this.courseCompletedOrRegistrationGap(enrollment._courseCompletedDate) <= 365)
      );
    },

    courseEndDateinFuture(enrollment) {
      return (
        new Date(enrollment._courseInfo._courseEndDate) >= new Date() &&
        (enrollment._enrollmentMode || '').toLowerCase() === 'verified' &&
        (enrollment._enrollmentStatus || '').toLowerCase() === 'active'
      );
    },

    courseEndDateinPastWithCreditEligible(enrollment) {
      return (
        new Date(enrollment._courseInfo._courseEndDate) < new Date() &&
        (enrollment._enrollmentMode || '').toLowerCase() === 'verified' &&
        (enrollment._enrollmentStatus || '').toLowerCase() === 'active' &&
        (enrollment._creditEligible || '').toLowerCase() === 'y' &&
        (enrollment._creditConverted || '').toLowerCase() !== 'y'
      );
    },

    // Between the course end date and grade finalization, a verified active
    // enrollment has no credit-eligibility determination yet (Credit_eligible is empty),
    // so it fell through every branch and the GPA estimator showed "No courses found".
    courseEndDateinPastWithCreditPending(enrollment) {
      const finalisedDate = enrollment._courseInfo._courseFinalisedDate;

      return (
        new Date(enrollment._courseInfo._courseEndDate) < new Date() &&
        (enrollment._enrollmentMode || '').toLowerCase() === 'verified' &&
        (enrollment._enrollmentStatus || '').toLowerCase() === 'active' &&
        !enrollment._courseCompletedDate &&
        !(enrollment._creditEligible || '').trim() &&
        (enrollment._creditConverted || '').toLowerCase() !== 'y' &&
        !!finalisedDate &&
        new Date() < this.addDays(new Date(finalisedDate), 1)
      );
    },

    addDays(date, days) {
      const result = new Date(date);

      result.setDate(result.getDate() + days);

      return result;
    },

    continuousCourseCheckForGpaCourseCalculator(enrollment) {
      // enrollment mode = verified
      // AND
      // enrollment status = active
      // AND
      // Credit Converted = null or N
      // AND
      // Registration Date is less than 365 days ago
      return (
        (enrollment._courseInfo._courseType || '').toLowerCase() === 'continuous' &&
        (enrollment._enrollmentMode || '').toLowerCase() === 'verified' &&
        (enrollment._enrollmentStatus || '').toLowerCase() === 'active' &&
        (enrollment._creditConverted || '').toLowerCase() !== 'y' &&
        this.courseCompletedOrRegistrationGap(enrollment._courseInfo._courseRegistrationDate) <= 365
      );
    },

    courseCompletedOrRegistrationGap(date) {
      var completedDate = new Date(date);
      var today = new Date();

      return Math.abs(today - completedDate) / (1000 * 60 * 60 * 24);
    },

    logInfo(message, data) {
      if (this.verbose) {
        if (data !== undefined) {
          console.info(`[info] ${window.location.href} ${message}`, data);
        } else {
          console.info(`[info] ${window.location.href} ${message}`);
        }

        return;
      }
      console.info(`[info] ${message}`);
    },

    logError(message, e) {
      if (this.verbose) {
        console.error(`[error] ${window.location.href} ${message}`, e);

        return;
      }
      console.error(`[error] ${message}`, e);
    },

    processEnrollments(enrollments, cb) {
      const { coursesStore } = this.getStores();
      // Initialize global_course_sections array (temporary migration approach)
      // Always reset the array to prevent duplication when navigating back
      window.global_course_sections = [];
      let currentCourses = [],
        upcomingCourses = [],
        transcriptedCourses = [],
        transcriptCourses = [],
        inactiveCourses = [],
        gpaCourses = [],
        enrolledCourses = [],
        currDate = new Date();

      // TODO: If you need to move this logic in future, consider flagging each enrollment according to the section and here you can just render
      enrollments.forEach((e) => {
        let enrollmentDatePlusYear = '';

        if (e._enrollmentModeVerifiedDate || e._enrollmentDate) {
          let enrollmentDate = new Date(e._enrollmentModeVerifiedDate || e._enrollmentDate);

          enrollmentDatePlusYear = enrollmentDate.setFullYear(enrollmentDate.getFullYear() + 1);
        }

        if ((e._enrollmentStatus || '').toLowerCase() !== 'inactive') {
          if ((e._courseInfo || {})._courseName) {
            if (e._courseCompletedDate && e._creditEligible === 'Y') {
              let courseCompletedDate = new Date(e._courseCompletedDate); //eslint-disable-line

              let datePlusYear = courseCompletedDate.setFullYear(courseCompletedDate.getFullYear() + 1);

              datePlusYear = new Date(datePlusYear).setDate(new Date(datePlusYear).getDate() + 1);

              if (e._transcriptedDate) {
                transcriptedCourses.push(e);
              } else if (
                e._creditConverted === 'Y' &&
                (e._courseInfo._creditConversionStatus || '').toLowerCase() === 'retry'
              ) {
                // Courses with "retry" status should appear in Add to Transcript
                transcriptCourses.push(e);
              } else if (e._creditWindowOverride) {
                // ULC-3378: approved override keeps the course addable past the window
                transcriptCourses.push(e);
              } else if (
                this.isDateInPast(
                  new Date(currDate.toLocaleString('en-US', { timeZone: 'America/Phoenix' })),
                  new Date(datePlusYear)
                )
              ) {
                transcriptCourses.push(e);
              } else if (
                !this.isDateInPast(
                  new Date(currDate.toLocaleString('en-US', { timeZone: 'America/Phoenix' })),
                  new Date(datePlusYear)
                )
              ) {
                inactiveCourses.push(e);
              }
            } else if (
              e._courseInfo._courseType === 'Standard' &&
              this.isStandardCourseActive(currDate, e._courseInfo) &&
              e._buttonAction.toLowerCase() !== 'access no longer available'
            ) {
              currentCourses.push(e);
              // OS-4250 Continuous Courses With An End Date Do Not Come Off The Dashboard As Expected Start
            } else if (
              (e._courseInfo._courseType === 'Continuous' &&
                e._courseInfo._courseEndDate === '' &&
                (e._creditEligible || '').toLowerCase() !== 'y' &&
                // +ve value is in the past
                // OS-5091 changed from enrollmentDate to enrollmentVerifiedDate
                parseInt(Helper.methods.calculateAge(e._enrollmentModeVerifiedDate)) > 365) ||
              e._buttonAction.toLowerCase() === 'access no longer available'
            ) {
              inactiveCourses.push(e);
            } else if (
              e._courseInfo._courseType === 'Continuous' &&
              e._courseInfo._courseEndDate !== '' &&
              // Check If We Are Past The Course Finalized As Of Date Or Course End Date And Credit Eligible
              // +ve value is in the past
              parseInt(Helper.methods.calculateAge(e._courseInfo._courseFinalisedDate)) > 0 &&
              parseInt(Helper.methods.calculateAge(e._courseInfo._courseEndDate)) > 0 &&
              (e._creditEligible || '').toLowerCase() !== 'y'
            ) {
              inactiveCourses.push(e);
              // OS-4250 Continuous Courses With An End Date Do Not Come Off The Dashboard As Expected End
            } else if (
              (enrollmentDatePlusYear &&
                e._courseInfo._courseType === 'Continuous' &&
                this.isDateInFuture(currDate, new Date(e._courseInfo._courseRegistrationDate))) ||
              (e._courseInfo._courseType === 'Audit' && !e._courseCompletedDate)
            ) {
              currentCourses.push(e);
            } else if (e._courseInfo._courseType === 'Audit' && e._courseCompletedDate) {
              inactiveCourses.push(e);
            } else if (this.isDateInPast(currDate, new Date(e._courseInfo._courseStartDate))) {
              upcomingCourses.push(e);
            } else {
              inactiveCourses.push(e);
            }
          }
        } else {
          inactiveCourses.push(e);
        }

        Helper.methods.sortCoursesWithAuditType(currentCourses);

        Helper.methods.sortCoursesWithAuditType(inactiveCourses);

        if (this.isGpaCourseCalculatorEligible(e)) {
          gpaCourses.push(e);
        }

        if (e._isCourseSelected) enrolledCourses.push(e);
      });

      // ACC-439: preregistered OD courses are not enrollments, so they are never in
      // `enrollments` and cannot be bucketed by the loop above. They join here, before the
      // sort, so the section stays in start-date order across both sources.
      upcomingCourses = mergePreregisteredUpcoming(upcomingCourses, coursesStore.preregisteredCourses, enrollments);

      // https://asudev.jira.com/browse/OS-4606
      // List upcoming courses sort by course start date
      upcomingCourses.sort(Helper.methods.sortCoursesWithStartDate);
      currentCourses.sort(Helper.methods.sortCurrentCourses);

      // Prepare list of course sections
      coursesStore.setGPACourses(gpaCourses);
      coursesStore.enrolledCourses = enrolledCourses;

      window.global_course_sections.push({
        section_title: 'My Current Courses',
        courses: currentCourses,
        shouldShowViewCourse: true
      });
      window.global_course_sections.push({
        section_title: 'Add to Transcript',
        courses: transcriptCourses,
        shouldShowViewCourse: true
      });
      window.global_course_sections.push({
        section_title: 'Transcripted Courses',
        courses: transcriptedCourses,
        shouldShowViewCourse: false
      });
      window.global_course_sections.push({
        section_title: 'My Upcoming Courses',
        courses: upcomingCourses,
        shouldShowViewCourse: true
      });
      window.global_course_sections.push({
        section_title: 'Past Courses',
        courses: inactiveCourses,
        shouldShowViewCourse: false
      });

      coursesStore.setGlobalCourseSections(window.global_course_sections);

      this.degreePaths = this.$conn.getDegreePaths();

      // Ensure window.global_course_sections is a valid array before filtering
      const courseSectionsArray = Array.isArray(window.global_course_sections) ? window.global_course_sections : [];

      // Check if course_sections is a prop (which would be read-only)
      // If it's a prop, skip updating it - the parent will handle it
      const isCourseSectionsProp = this.$options.props && this.$options.props.course_sections;

      if (!isCourseSectionsProp) {
        const filteredSections = courseSectionsArray.filter((e) => {
          return (
            e &&
            e.section_title &&
            ['My Current Courses', 'Add to Transcript', 'My Upcoming Courses'].indexOf(e.section_title) >= 0
          );
        });
        this.course_sections = filteredSections;
      }

      if (typeof cb === 'function') cb(enrollments);
    },

    processEnrollmentsPathways(enrollments, pathwayCourses, cb) {
      const date = new Date();
      let day = date.getDate() - 7;
      date.setDate(day);
      const { coursesStore } = this.getStores();
      // Initialize global_pathway_courses_sections array (temporary migration approach)
      // Always reset the array to prevent duplication when navigating back
      window.global_pathway_courses_sections = [];

      let currentCourses = [];
      let openCourses = [];
      let notOpenCourses = [];
      let transcriptedOrToBeTranscriptedCourses = [];
      let gpaCourses = [];
      let enrolledCourses = [];

      const currDate = new Date();

      enrollments.forEach((e) => {
        let shouldRemoveFromPathwayCourses = false;
        let enrollmentDatePlusYear = '';

        if (e._enrollmentDate) {
          let enrollmentDate = new Date(e._enrollmentDate);

          enrollmentDatePlusYear = enrollmentDate.setFullYear(enrollmentDate.getFullYear() + 1);
        }

        if (e._enrollmentStatus && e._enrollmentStatus.toLowerCase() !== 'inactive') {
          if (e._courseInfo._courseName) {
            if (e._courseCompletedDate) {
              let courseCompletedDate = new Date(e._courseCompletedDate);
              let datePlusYear = courseCompletedDate.setFullYear(courseCompletedDate.getFullYear() + 1);
              if (
                e._numberOfCreditsEarned &&
                (e._transcriptedDate ||
                  (e._creditConverted === 'Y' && (e._enrollmentMode || '').toLowerCase() === 'credit') ||
                  (e._creditConverted === 'Y' &&
                    ((e._courseInfo._creditConversionStatus || '').toLowerCase() === 'pending' ||
                      (e._courseInfo._creditConversionStatus || '').toLowerCase() === 'approved' ||
                      e._courseInfo._creditConversionStatus === null)))
              ) {
                transcriptedOrToBeTranscriptedCourses.push(e);

                shouldRemoveFromPathwayCourses = true;
              } else {
                if (this.isDateInPast(currDate, new Date(datePlusYear))) {
                  if (
                    (e._creditEligible || '').toLowerCase() === 'y' &&
                    (!e._creditConverted || e._creditConverted === 'N') &&
                    (e._creditConversionStatus || '').toLowerCase() !== 'approved'
                  ) {
                    currentCourses.push(e);

                    shouldRemoveFromPathwayCourses = true;
                  } else if (e._numberOfCreditsEarned && (e._creditEligible || '').toLowerCase() === 'y') {
                    transcriptedOrToBeTranscriptedCourses.push(e);

                    shouldRemoveFromPathwayCourses = true;
                  }
                }
              }
            } else if (
              (e._enrollmentMode || '').toLowerCase() === 'honor' ||
              (e._enrollmentMode || '').toLowerCase() === 'verified'
            ) {
              if (
                ((e._enrollmentMode || '').toLowerCase() === 'verified' &&
                  (e._creditEligible || '').toLowerCase() === 'y' &&
                  !e._creditConverted) ||
                (e._courseInfo._courseType === 'Standard' &&
                  this.isDateInFuture(
                    new Date(e._courseInfo._courseFinalisedDate || e._courseInfo._courseEndDate),
                    currDate
                  )) ||
                this.isDateInPresent(
                  new Date(e._courseInfo._courseFinalisedDate || e._courseInfo._courseEndDate),
                  currDate
                ) ||
                (enrollmentDatePlusYear &&
                  !e._courseCompletedDate &&
                  e._courseInfo._courseType === 'Continuous' &&
                  this.isDateInPast(currDate, new Date(enrollmentDatePlusYear)))
              ) {
                currentCourses.push(e);

                shouldRemoveFromPathwayCourses = true;
              }
            } else if ((e._buttonAction || '').toLowerCase() === 'view transcript') {
              transcriptedOrToBeTranscriptedCourses.push(e);

              shouldRemoveFromPathwayCourses = true;
            }
          }
        } else if (!e._enrollmentStatus && e._enrollmentDate) {
          currentCourses.push(e);

          shouldRemoveFromPathwayCourses = true;
        }

        if (this.isGpaCourseCalculatorEligible(e)) {
          gpaCourses.push(e);
        }

        if (e._isCourseSelected) enrolledCourses.push(e);

        if (shouldRemoveFromPathwayCourses) {
          pathwayCourses = (pathwayCourses || []).filter((pathwayCourse) => {
            return pathwayCourse._coursePrefix !== e._courseInfo._coursePrefix;
          });
        }
      });

      Helper.methods.sortCoursesWithAuditType();

      pathwayCourses.forEach((e) => {
        let isAtleastOneOfferingActive =
          (e._courseOfferingData || []).filter((j) => (j || {})._courseOfferingActive).length > 0;

        isAtleastOneOfferingActive && !e.isMatchedEnrollmentFound ? openCourses.push(e) : notOpenCourses.push(e);
      });
      coursesStore.setGPACourses(gpaCourses);
      coursesStore.enrolledCourses = enrolledCourses;
      var remainingCourses = { notOpenCourses, openCourses };

      window.global_pathway_courses_sections.push({
        section_title: 'Courses in Progress',
        courses: currentCourses,
        shouldShowViewCourse: true
      });

      window.global_pathway_courses_sections.push({
        section_title: 'Courses Remaining',
        courses: remainingCourses,
        shouldShowViewCourse: true
      });

      window.global_pathway_courses_sections.push({
        section_title: 'Courses Completed',
        courses: transcriptedOrToBeTranscriptedCourses,
        shouldShowViewCourse: true
      });

      this.degreePaths = this.$conn.getDegreePaths();

      this.pathway_courses_sections = (window.global_pathway_courses_sections || []).filter((e) => {
        return ['Courses in Progress', 'Courses Remaining', 'Courses Completed'].indexOf(e.section_title) >= 0;
      });

      const { pathwaysStore, userStore } = this.getStores();
      pathwaysStore.setPathwayTotalMandateCourse(
        Helper.methods.calculateMandatePathwayCourse(
          pathwaysStore.pathwayDegreeCourses.pathwayCourses,
          userStore.apis.learnerData._birthDate
        )
      );

      if (typeof cb === 'function') cb(enrollments);
    },

    isDateInPast(dateToCompare, futureDate) {
      return dateToCompare.getTime() < futureDate.getTime();
    },

    isDateInFuture(dateToCompare, futureDate) {
      return dateToCompare.getTime() >= futureDate.getTime();
    },

    isDateInPresent(dateToCompare, currentData) {
      return dateToCompare.getTime() === currentData.getTime();
    },

    isDateBetween(dateToCompare, startDate, endDate) {
      return dateToCompare.getTime() > startDate.getTime() && dateToCompare.getTime() < endDate.getTime();
    },

    isDateTodayOrInPast(dateToCompare, futureDate) {
      const d1 = new Date(new Date(dateToCompare).toDateString());
      const d2 = new Date(new Date(futureDate).toDateString());
      return d1.getTime() <= d2.getTime();
    },

    isStandardCourseActive(currDate, courseInfo) {
      const startDate = new Date(courseInfo._courseStartDate);
      const finalisedDate = courseInfo._courseFinalisedDate ? new Date(courseInfo._courseFinalisedDate) : null;
      const endDate = finalisedDate && finalisedDate > startDate ? finalisedDate : new Date(courseInfo._courseEndDate);

      return this.isDateBetween(currDate, startDate, endDate);
    },

    weeksBetween(d1, d2) {
      return Math.round((d2 - d1) / (7 * 24 * 60 * 60 * 1000));
    },

    // Whether this load asks the enrollments read for the learner's queued OD runs (flag
    // OD_PREREGISTRATION_UPCOMING). A dashboard renders fine without them.
    shouldIncludeScheduled() {
      if (!isFeatureEnabled('OD_PREREGISTRATION_UPCOMING')) return false;

      // A persisted view-as session this page load hasn't resumed yet: the proxy isn't
      // applied, so the read would go out as the admin and its result be discarded when
      // the restore refetches. Skip it — enterViewAs re-runs this path with the proxy on.
      // A session belonging to a different login is about to be cleared, not resumed, so
      // it doesn't defer the read (mirrors the mismatch check in restoreViewAsSession).
      const viewAsStore = useViewAsStore();
      const currentAsurite = (this.$conn.learnerData && this.$conn.learnerData._asuRite) || '';
      const resumingViewAs =
        viewAsStore.hasPendingSession &&
        !this.$conn._isProxy &&
        !(viewAsStore.actorAsurite && currentAsurite && viewAsStore.actorAsurite !== currentAsurite);

      return !resumingViewAs;
    },

    // The queued OD runs the enrollments read carried, or [] — never throws, never
    // redirects. api-hub normalises a failed read onto `code`, so branch on that rather than
    // on status text. A failure is still reported: swallowed, it read as "this learner has none".
    preregisteredRunsFrom(scheduled) {
      if (!scheduled) return [];
      if (!scheduled.error) return scheduled.runs || [];

      const code = scheduled.error.code || 'AETHER_UPSTREAM_ERROR';
      const status = scheduled.error.status ?? null;

      // A learner with no Aether profile has nothing queued. That is an empty state, not
      // a fault, and reporting it would bury the real failures in noise.
      if (code === 'AETHER_LEARNER_NOT_FOUND') {
        this.logInfo('no preregistered courses: learner has no Aether profile');

        return [];
      }

      // Hand-picked fields only: this payload leaves the ASU domain. The code and status are
      // the whole diagnosis.
      captureMessage(`scheduled preregistrations unavailable (${code})`, {
        level: status && status < 500 ? 'warning' : 'error',
        logToConsole: true,
        context: {
          tags: { feature: 'od-preregistration', aetherErrorCode: code },
          extra: { status }
        }
      });

      return [];
    },

    // Projects the runs against the course catalog and parks them in the store, which is
    // where processEnrollments reads them from. Call it before processEnrollments so the
    // section is built once, with both sources in it.
    applyPreregisteredRuns(runs) {
      const { coursesStore } = this.getStores();

      coursesStore.setPreregisteredCourses(
        projectPreregisteredRuns(runs, { catalog: this.$conn.courseList?.courses || [] })
      );
    },

    getEnrollmentsAndCourses(cb) {
      this.logInfo('get enrollments and courses');

      // The enrollments read carries the queued OD runs; they are applied after the
      // Promise.all so a run the read carried no catalog entry for can use the loaded catalog.
      Promise.all([
        this.$conn.getDashboardPageData({ includeScheduled: this.shouldIncludeScheduled() }),
        this.$conn.getCourseList()
      ])
        .then(async (res) => {
          this.logInfo('get enrollments and courses success', res);

          const { userStore } = this.getStores();
          if (userStore) {
            this.logInfo('getting session vars from store');

            this.earnedCredits = ((userStore.apis || {}).learnerData || {})._numberOfCreditsCompleted || 0;
            this.totalCredits = ((userStore.apis || {}).learnerData || {})._numberOfCreditsNeeded || 0;
            // Only set currentGPA if it's not a computed property
            if (!this.$options.computed || !this.$options.computed.currentGPA) {
              this.currentGPA = parseFloat(((userStore.apis || {}).learnerData || {})._currentGPA || 0).toFixed(1);
            }
          }

          this.applyPreregisteredRuns(this.preregisteredRunsFrom(this.$conn.enrollmentList.scheduled));

          this.processEnrollments(this.$conn.enrollmentList.enrollments || [], cb);

          // Mark courses that align with user's pathway (uses lightweight /api/pathways/course-prefixes endpoint)
          if (typeof this.markPathwayCourses === 'function') {
            await this.markPathwayCourses();
          }
        })
        .catch((error) => {
          this.logError('get enrollment and courses failed', error);

          this.$router.push({ name: 'server-error' });
        });
    },

    getBasicAndDemograhicInfo() {
      this.demographicInfo = this.$conn.getDemographicInformation();
      this.basicLearnerInfo = this.$conn.getBasicLearnerInfo();

      // Don't mutate collectDemographicInfo here - let individual components handle it
      // this.collectDemographicInfo = (this.$conn.learnerData || {})._collectDemographicInfo;
    },

    getLearnersData() {
      this.learnerData = this.$conn.getBasicLearnerInformation();
      // Don't mutate collectDemographicInfo here - let individual components handle it
      // this.collectDemographicInfo = this.$conn.learnerData._collectDemographicInfo;
    },

    // For a component whose page load is run by a parent (DashboardWrapper): the same
    // projection getEnrollments ends with, applied to the data the parent's load left on the
    // connector and stores, so the page fetches enrollments and preregistrations only once.
    async awaitPageLoad(load, callFrom, cb, parallelWork) {
      if (!load) return this.init(callFrom, cb, parallelWork);

      if (parallelWork) parallelWork();
      await load;

      const { userStore } = this.getStores();
      this.earnedCredits = userStore.apis?.learnerData?._numberOfCreditsCompleted || 0;
      this.totalCredits = userStore.apis?.learnerData?._numberOfCreditsNeeded || 0;
      if (!this.$options.computed || !this.$options.computed.currentGPA) {
        this.currentGPA = parseFloat(((userStore.apis || {}).learnerData || {})._currentGPA || 0).toFixed(1);
      }

      this.processEnrollments(this.$conn.enrollmentList?.enrollments || [], cb);

      if (typeof this.markPathwayCourses === 'function') {
        this.markPathwayCourses();
      }
    },

    async getEnrollments(cb, callFrom, parallelWork) {
      this.logInfo('get enrollments');
      try {
        // The queued OD runs ride on this read too, so they refresh whenever it runs on its own
        // (post-unenroll) and a stale list never outlives a fired run. The catalog is not
        // fetched here: each run carries its own catalog entry, and one the partner catalog
        // lacks falls back to the offering's own name and prefix.
        // Run parallelWork alongside enrollments fetch if provided
        const enrollmentPromise = this.$conn.getDashboardPageData({
          includeScheduled: this.shouldIncludeScheduled()
        });
        if (parallelWork) parallelWork();
        const res = await enrollmentPromise;

        this.logInfo('get enrollments success', res);

        const { userStore } = this.getStores();
        this.earnedCredits = userStore.apis?.learnerData?._numberOfCreditsCompleted || 0;
        this.totalCredits = userStore.apis?.learnerData?._numberOfCreditsNeeded || 0;
        // Only set currentGPA if it's not a computed property
        if (!this.$options.computed || !this.$options.computed.currentGPA) {
          this.currentGPA = parseFloat(((userStore.apis || {}).learnerData || {})._currentGPA || 0).toFixed(1);
        }

        this.applyPreregisteredRuns(this.preregisteredRunsFrom(this.$conn.enrollmentList.scheduled));

        // No catalog call even with no enrollments: nothing on these pages reads it, and the
        // queued runs carry their own catalog entries.
        this.processEnrollments(this.$conn.enrollmentList.enrollments || [], cb);

        // Mark courses that align with user's pathway (uses lightweight /api/pathways/course-prefixes endpoint)
        if (typeof this.markPathwayCourses === 'function') {
          this.markPathwayCourses();
        }
      } catch (err) {
        this.logError('get enrollments failed', err);

        this.$router.push({ name: 'server-error' });
      }
    },

    getEnrollmentAndPathwayDegrees(cb) {
      this.logInfo('get enrollments and pathway degree courses');

      Promise.all([
        this.$conn.getDashboardPageData(),
        this.$conn.getPathwayDegreeCourseList(),
        this.$conn.getCourseList()
      ])
        .then((res) => {
          const { pathwaysStore } = this.getStores();
          pathwaysStore.totalCourses = res[1]?.pathwayCourses?._pathwayCourses?.length || '';

          const response = this.matchEnrollmentAndPathwayCourses(res);

          this.processEnrollmentsPathways(response.enrollmentDetails, response.pathwayDetails || [], cb);
        })
        .catch((error) => {
          this.logError('get enrollment and courses failed', error);

          // Redirect to dashboard on error - pathway features show there
          this.$router.push({ name: 'dashboard' });
        });
    },

    getPathwayDegrees(cb) {
      this.logInfo('get pathway degree courses for dashboard');

      Promise.all([this.$conn.getPathwayDegreeCourseList()])
        .then((res) => {
          res[1] = res[0];
          res[0].enrollments = this.$conn.enrollmentList.enrollments;

          const { pathwaysStore } = this.getStores();
          pathwaysStore.totalCourses = res[1]?.pathwayCourses?._pathwayCourses?.length || '';

          const response = this.matchEnrollmentAndPathwayCourses(res);

          this.processEnrollmentsPathways(response.enrollmentDetails || [], response.pathwayDetails || [], cb);
        })
        .catch((error) => {
          this.logError('get pathway degree courses', error);
        });
    },

    matchEnrollmentAndPathwayCourses(res) {
      const pathwayEnrollments = [];

      const _enrollments = res[0].enrollments;
      const _pathwayCourses = res[1].pathwayCourses ? res[1].pathwayCourses._pathwayCourses : [];

      _pathwayCourses.forEach((pathwayCourse) => {
        _enrollments.forEach((_enrollment) => {
          for (const courseOffering of pathwayCourse._courseOfferingData || []) {
            if (courseOffering._coursePrefix === _enrollment._courseInfo._coursePrefix) {
              _enrollment._pathwayRequirement = pathwayCourse._pathwayRequirement;
              pathwayEnrollments.push(_enrollment);

              pathwayCourse.isMatchedEnrollmentFound = true;

              break;
            }
          }
        });
      });

      this.$conn.enrollmentList.pathwayEnrollments = pathwayEnrollments;
      this.$conn.enrollmentList.pathwayEnrollmentList = _pathwayCourses
        .map((_pathwayCourse) => {
          if (_pathwayCourse._courseOfferingData) {
            _pathwayCourse._courseOfferingData = [
              _pathwayCourse._courseOfferingData.filter((e) => {
                return e._courseOfferingActive;
              })[0] || _pathwayCourse._courseOfferingData[0]
            ];

            return _pathwayCourse;
          }
        })
        .filter((e) => e);

      return {
        pathwayDetails: this.$conn.enrollmentList._pathwayEnrollmentList,
        enrollmentDetails: this.$conn.enrollmentList._pathwayEnrollments
      };
    },

    isExistingUserSaveOpenedxIDLearner(err) {
      let self = this;

      if (err.response.data.errorMessage.includes('User already exists')) {
        const { userStore } = self.getStores();
        userStore.apis.learnerData._openEdxId = JSON.parse(
          err.response.data.errorMessage.substring(
            err.response.data.errorMessage.indexOf('{'),
            err.response.data.errorMessage.lastIndexOf('}') + 1
          )
        ).user_id;

        self.$conn.learnerData.openEdxId = userStore.apis.learnerData._openEdxId;

        self.$conn.saveOpenEdxId();
      } else {
        self.error =
          err.response && typeof err.response.data.errorMessage === 'string'
            ? err.response.data.errorMessage
            : 'An error occured.';
      }
    },

    getLearnersInfo(cb) {
      this.logInfo('get learners data');
      cb();
      setTimeout(() => {}, 500);
    },

    async init(callFrom, cb, parallelWork) {
      const self = this;

      if (!self.$auth.isAuthenticated()) {
        self.logInfo('going to login from init');

        return self.$router.push({ name: 'login' });
      }

      if (!self.$conn.learnerData) {
        self.logInfo('init loggedin session in mixin');

        try {
          const userInfo = await self.$conn.initSecureSession(true);

          if (userInfo?.data?.errorMessage) {
            self.logError('init loggedin session failed', userInfo.data);

            return self.$router.push({ name: 'server-error' });
          }
          const userStore = useUserStore();
          userStore.setPartnerData((self.$conn.partnerData || {}).welcomePageData);

          if (userInfo?.viewAs) self.$emit('userName', `${userInfo.firstName} ${userInfo.lastName}`);

          self.logInfo('init loggedin session success', userInfo);

          if ((userInfo?.eaOptIn || '').toLowerCase() === 'false') {
            self.$conn.setLearnerSub();

            self.$session.set('fromCallBack', true);
            self.$router.push({ name: 'Registerv2', query: { email: self.$conn.learnerData?._emailId } });
          } else {
            // Minor consent gate (ACC-352): resolve consent status before loading the portal.
            await self.ensureMinorConsentStatus();
            self.ensureHsGradDateStatus();

            if (callFrom === 'dashboard' || callFrom === 'courseHistory') {
              self.getEnrollments(cb, callFrom, parallelWork);
            } else if (callFrom === 'addCourses') {
              self.getEnrollmentsAndCourses(cb);
            } else if (callFrom === 'my-pathway' || callFrom === 'about-pathways') {
              self.getEnrollmentAndPathwayDegrees(cb);
            } else if (callFrom === 'profile') {
              self.getLearnersInfo(cb);
            } else {
              self.getEnrollmentsAndCourses(cb);
            }
          }
        } catch (err) {
          if (err.response) {
            // Handle 400 Bad Request errors specifically
            if (err.response.status === 400) self.logError('400 Bad Request error', err.response.data);

            // Note: NO_CONTACT_FOUND is now handled as 200 response in connector.service.js
            // No longer handling NO_CONTACT_FOUND in catch block since it's not an error anymore
            self.error =
              typeof err.response.data.errorMessage === 'string'
                ? JSON.parse(err.response.data.errorMessage).message
                : '';
          }

          self.logError('init loggedin session failed', err);
        }
      } else {
        if (self.$conn.learnerData._viewAs)
          self.$emit('userName', `${self.$conn.learnerData._firstName} ${self.$conn.learnerData._lastName}`);

        // ACC-352: the login flow preloads learnerData (Callback.vue), so the gate must
        // resolve here too — this is the branch a normal post-login dashboard init hits.
        await self.ensureMinorConsentStatus();
        self.ensureHsGradDateStatus();

        if (callFrom === 'dashboard' || callFrom === 'courseHistory') {
          self.getEnrollments(cb, callFrom, parallelWork);
        } else if (callFrom === 'addCourses') {
          self.getEnrollmentsAndCourses(cb);
        } else if (callFrom === 'my-pathway' || callFrom === 'about-pathways') {
          self.getEnrollmentAndPathwayDegrees(cb);
        } else if (callFrom === 'profile') {
          self.getLearnersInfo(cb);
        } else {
          self.getEnrollmentsAndCourses(cb);
        }
      }
    }
  }
};
